import { useMemo, useState, useEffect, useCallback } from 'react';

import { setJwtToHeaders, clearJwtFromHeaders } from 'src/utils/axios';

import { exchangeAuth0Login } from 'src/api/auth';
import { useGetUserAccount } from 'src/domains/user-account/api';

import { AuthContext } from './auth-context';
import { LoginOptions, LogoutOptions } from '../../types';
import {
  getSession,
  setSession,
  SessionUser,
  isValidToken,
  getSessionUser,
  consumeReturnTo,
  startAuth0Login,
  buildAuth0LogoutUrl,
} from './utils';

// ----------------------------------------------------------------------
// Login state is the Strapi JWT. Auth0 is only reached through the API's
// connect flow (startAuth0Login → … → Auth0CallbackPage → completeLogin).
// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  // null = not checked yet; true/false once the stored token has been checked
  const [jwtReady, setJwtReady] = useState<boolean | null>(null);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);

  const {
    userAccount: extraUserInfo,
    userAccountLoading,
    userAccountError,
    refreshUserAccount,
  } = useGetUserAccount(jwtReady === true);

  // Restore the login from the stored Strapi JWT
  useEffect(() => {
    const token = getSession();
    if (token && isValidToken(token)) {
      setSession(token);
      setJwtToHeaders(token); // also marks auth as ready for SWR
      setSessionUser(getSessionUser());
      setJwtReady(true);
    } else {
      setSession(null);
      clearJwtFromHeaders();
      setJwtReady(false);
    }
  }, []);

  useEffect(() => {
    if (userAccountError) {
      console.error('User account fetch error (will retry automatically):', userAccountError);
    }
  }, [userAccountError]);

  // LOGIN
  const handleLoginWithRedirect = useCallback(async (options?: LoginOptions) => {
    startAuth0Login(options?.appState?.returnTo);
  }, []);

  /** Called by Auth0CallbackPage once Strapi redirects back. Returns the page to open next. */
  const completeLogin = useCallback(async () => {
    const { jwt, user } = await exchangeAuth0Login();
    setSession(jwt, user);
    setJwtToHeaders(jwt);
    setSessionUser(user);
    setJwtReady(true);
    return consumeReturnTo();
  }, []);

  // LOGOUT
  const handleLogout = useCallback(async (options?: LogoutOptions) => {
    const returnTo = options?.logoutParams?.returnTo ?? window.location.origin;

    setSession(null);
    clearJwtFromHeaders();
    setSessionUser(null);
    setJwtReady(false);

    // End the Auth0 session too, otherwise the next login skips the login form
    window.location.assign(buildAuth0LogoutUrl(returnTo) ?? returnTo);
  }, []);

  // ----------------------------------------------------------------------

  const authenticated = jwtReady === true;
  const loading = jwtReady === null || (authenticated && userAccountLoading);

  const handlePerformActionOrLogin = useCallback(
    async (e: React.MouseEvent, callback: () => void) => {
      e.preventDefault();
      if (authenticated) {
        return callback();
      }
      return handleLoginWithRedirect({ appState: { returnTo: window.location.pathname } });
    },
    [authenticated, handleLoginWithRedirect]
  );

  const memoizedValue = useMemo(() => {
    const fullName = [extraUserInfo?.name, extraUserInfo?.last_name].filter(Boolean).join(' ');

    return {
      user: authenticated
        ? {
            email: sessionUser?.email,
            displayName: fullName || extraUserInfo?.username || sessionUser?.username || '',
            photoURL: null,
            role: 'authenticated',
            ...extraUserInfo,
            id: extraUserInfo?.id ?? sessionUser?.id ?? 0,
          }
        : null,
      method: 'auth0',
      loading,
      authenticated,
      unauthenticated: jwtReady === false,
      //
      loginWithRedirect: handleLoginWithRedirect,
      // Popup login isn't supported by the API's connect flow; it redirects instead
      loginWithPopup: handleLoginWithRedirect,
      completeLogin,
      logout: handleLogout,
      handlePerformActionOrLogin,
      refreshUserAccount,
    };
  }, [
    authenticated,
    extraUserInfo,
    sessionUser,
    loading,
    jwtReady,
    handleLoginWithRedirect,
    completeLogin,
    handleLogout,
    handlePerformActionOrLogin,
    refreshUserAccount,
  ]);

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
